import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTelegram } from '../../context/TelegramContext';
import { useCurrency } from '../../context/CurrencyContext';
import styles from '../../styles/components/Modal.module.css';
import { getUserId } from '../../services/api';

// Фирменные цвета из variables.module.css
const INCOME_COLOR = '#e0f0ff';
const INCOME_TEXT = '#337ab7';
const EXPENSE_COLOR = '#ffe0b3';
const EXPENSE_TEXT = '#c0392b';

// Обновленное модальное окно с улучшенным центрированием для Samsung
const TelegramModal = styled.div`
  width: 96%;
  max-width: 450px;
  background: linear-gradient(145deg, #ffffff, #f5f6fa);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 
    5px 5px 15px rgba(0, 0, 0, 0.08),
    -5px -5px 15px rgba(255, 255, 255, 0.8);
  
  /* Явное центрирование для всех устройств */
  margin: 0 auto;
  position: relative;
  left: 0; /* Сброс любых потенциальных смещений */
  transform: none; /* Отключаем трансформации, которые могут влиять на центрирование */
  
  @media (max-width: 400px) {
    width: 100%;
    height: 100%;
    max-height: 100vh;
    border-radius: 0;
    overflow-x: hidden;
  }
`;

// Обновляем контейнер модального окна для Samsung и других устройств
const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  overflow: hidden;
  
  /* Исправление для Samsung - принудительное центрирование */
  & > * {
    margin: 0 auto; /* Центрирование дочерних элементов */
  }
`;


// Обновим стили для ScrollIndicator чтобы он не вызывал переполнение
const ScrollIndicator = styled.div`
  display: none;
  /* Удаляем абсолютное позиционирование, если оно есть,
     чтобы предотвратить выход за пределы контейнера */
`;
// Улучшенный заголовок с фирменными цветами и неоморфными эффектами
const ModalHeader = styled.div`
  background: ${props => props.type === 'income' ? 'linear-gradient(145deg, #e0f0ff, #d5e6f5)' : 'linear-gradient(145deg, #ffe0b3, #f5d6a9)'};
  color: ${props => props.type === 'income' ? INCOME_TEXT : EXPENSE_TEXT};
  padding: 16px;
  position: relative;
  text-align: center;
  border-bottom: 1px solid ${props => props.type === 'income' ? '#c8e4ff' : '#ffd499'};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  text-shadow: 1px 1px 1px rgba(255, 255, 255, 0.6);
`;

const CloseIcon = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: ${props => props.type === 'income' ? INCOME_TEXT : EXPENSE_TEXT};
  font-size: 22px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  box-shadow: 
    2px 2px 5px rgba(0, 0, 0, 0.05),
    -2px -2px 5px rgba(255, 255, 255, 0.4);
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
    box-shadow: 
      inset 1px 1px 3px rgba(0, 0, 0, 0.1),
      inset -1px -1px 3px rgba(255, 255, 255, 0.5);
  }
`;


// Улучшенная группа элементов формы с явными отступами
const FormGroup = styled.div`
  margin-bottom: 4px;
  padding: 0;
  width: 100%;
  box-sizing: border-box;
`;

// Модифицированное поле для комментария с корректными отступами
const CommentTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px 16px;
  border: none;
  border-radius: 12px;
  background: #ffffff;
  resize: none;
  font-size: 16px; /* Важно: Minimum 16px для iOS чтобы предотвратить зум */
  transition: all 0.3s;
  box-shadow: 
    inset 2px 2px 5px rgba(0, 0, 0, 0.05),
    inset -2px -2px 5px rgba(255, 255, 255, 0.7);
  color: #444;
  margin: 0; /* Сбрасываем любые возможные внешние отступы */
  box-sizing: border-box; /* Гарантирует, что padding не увеличит ширину */
  
  &:focus {
    outline: none;
    box-shadow: 
      inset 3px 3px 6px rgba(0, 0, 0, 0.08),
      inset -3px -3px 6px rgba(255, 255, 255, 0.8);
  }
  
  &::placeholder {
    color: #aaa;
    font-size: 16px;
  }
`;

// Оптимизированное тело модального окна с явными отступами для всех дочерних элементов
const ModalBody = styled.div`
  padding: 20px;
  max-height: 75vh;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #f5f6fa;
  width: 100%;
  box-sizing: border-box;
  
  /* Центрирование содержимого внутри тела модального окна */
  margin: 0 auto;
  
  /* Гарантируем отступы для всех прямых потомков */
  & > * {
    width: 100%;
    box-sizing: border-box;
    padding-left: 0;
    padding-right: 0;
  }
  
  @media (max-width: 400px) {
    max-height: calc(100vh - 54px);
  }
`;

// Контейнер для обёртки поля комментария с явными отступами
const CommentContainer = styled.div`
  width: 100%;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
`;

// Улучшенный контейнер для поля ввода суммы и валюты в неоморфном стиле
const AmountInputContainer = styled.div`
  display: flex;
  position: relative;
  margin-bottom: 16px;
  width: 100%;
`;

// Обновленная обертка для поля суммы с интегрированным селектором валюты
const AmountInputWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: stretch;
  border-radius: 12px;
  background: #ffffff;
  transition: all 0.3s;
  overflow: hidden;
  box-shadow: 
    inset 2px 2px 5px rgba(0, 0, 0, 0.05),
    inset -2px -2px 5px rgba(255, 255, 255, 0.7);
  
  &:focus-within {
    box-shadow: 
      inset 3px 3px 6px rgba(0, 0, 0, 0.08),
      inset -3px -3px 6px rgba(255, 255, 255, 0.8);
  }
`;

// Обновленное поле ввода суммы
const AmountInput = styled.input`
  width: 100%;
  height: 52px;
  padding: 10px 16px;
  border: none;
  font-size: 20px;
  background: transparent;
  flex: 1;
  color: #444;
  
  &:focus {
    outline: none;
  }
`;

// Улучшенный селектор валюты с исправленным центрированием для iOS
const CurrencySelector = styled.select`
  height: 100%;
  min-height: 52px;
  width: 60px;
  padding: 0;
  border: none;
  background: ${props => props.type === 'income' ? 'rgba(224, 240, 255, 0.5)' : 'rgba(255, 224, 179, 0.5)'};
  color: ${props => props.type === 'income' ? INCOME_TEXT : EXPENSE_TEXT};
  font-weight: 500;
  appearance: none;
  -webkit-appearance: none; /* Явно указываем для Safari */
  cursor: pointer;
  font-size: 16px;
  border-left: 1px solid #eee;
  text-align: center;
  text-align-last: center; /* Для некоторых браузеров */
  -webkit-text-align-last: center; /* Для Safari */
  text-indent: 0; /* Отменяет неявные отступы в Safari */
  padding-left: 0; /* Убираем отступ слева для iOS */
  padding-right: 0; /* Убираем отступ справа для iOS */
  margin: 0;
  position: relative;
  transition: all 0.2s;
  box-sizing: border-box;
  direction: ltr; /* Гарантирует правильное направление текста */
  
  /* Исправление центрирования для iOS */
  @supports (-webkit-touch-callout: none) {
    /* Специальные стили только для iOS */
    text-indent: -4px; /* Корректировка для Safari на iOS */
    padding-right: 4px; /* Смещаем немного вправо для баланса */
  }
  
  &:focus {
    outline: none;
    background: ${props => props.type === 'income' ? 'rgba(224, 240, 255, 0.7)' : 'rgba(255, 224, 179, 0.7)'};
  }
  
  /* Убираем стандартную стрелку для всех браузеров */
  &::-ms-expand {
    display: none;
  }
`;

// Добавляем псевдоэлемент для стилизованной стрелки
const CurrencyWrapper = styled.div`
  position: relative;
  height: 100%;
  
  &::after {
    content: "";
    position: absolute;
    right: 50%;
    top: 50%;
    transform: translate(50%, -25%);
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid ${props => props.type === 'income' ? INCOME_TEXT : EXPENSE_TEXT};
    pointer-events: none;
  }
`;
// Улучшенная кнопка выбора даты с расширенной областью клика для iOS
const DateButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  padding: 0 16px;
  border-radius: 12px;
  background: #ffffff;
  color: #444;
  font-size: 14px;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
  margin-bottom: 16px;
  box-shadow: 
    2px 2px 5px rgba(0, 0, 0, 0.03),
    -2px -2px 5px rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
  
  /* Обеспечиваем правильное взаимодействие с дочерними элементами */
  z-index: 1;
  
  &:hover {
    background: linear-gradient(145deg, #ffffff, #f5f6fa);
  }
  
  &:active {
    box-shadow: 
      inset 2px 2px 5px rgba(0, 0, 0, 0.05),
      inset -2px -2px 5px rgba(255, 255, 255, 0.7);
  }
`;

// Скрытое поле для выбора даты с улучшенной обработкой событий
const DateInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  z-index: 2;
  cursor: pointer;
  
  /* Специфические стили для iOS - принудительно устанавливаем указатель */
  -webkit-tap-highlight-color: transparent;
  
  /* Отключаем действия по умолчанию для iOS */
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  
  /* Очень важно для iOS - предотвращаем стандартное поведение */
  -webkit-appearance: none;
  appearance: none;
`;

// Добавьте обертку для календаря, чтобы предотвратить перехват событий клика
const DateContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  /* Обеспечиваем неперехватывание событий клика */
  pointer-events: none;
  
  /* Сдвигаем весь контент чуть ниже для лучшего визуального центрирования */
  padding-top: 2px;
`;


// Заголовок секции в едином стиле
const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #4a6fa1;
  margin-bottom: 10px;
  opacity: 0.9;
`;




// Обновленный контейнер категорий с отлаженными стилями
const CategoryChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px; /* Увеличен отступ снизу для точек */
  box-sizing: border-box;
  width: 100%;
  
  @media (max-width: 480px) {
    /* На мобильных переключаемся на горизонтальную прокрутку */
    flex-wrap: nowrap;
    overflow-x: auto;
    padding: 5px 0 16px; /* Увеличенный нижний отступ для точек */
    -webkit-overflow-scrolling: touch;
    position: relative;
    
    /* Скрываем стандартную полосу прокрутки */
    scrollbar-width: none;
    -ms-overflow-style: none;
    
    &::-webkit-scrollbar {
      display: none;
    }
    
    /* Простой градиентный намек справа */
    &:after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 16px; /* Оставляем место для точек */
      width: 25px;
      pointer-events: none;
      background: linear-gradient(to right, rgba(245, 246, 250, 0), rgba(245, 246, 250, 0.95));
      opacity: ${props => props.hasMoreItems ? 1 : 0};
    }
  }
`;

// Более заметное визуальное выделение для активной категории
const CategoryChip = styled.button`
  padding: 8px 12px;
  border: none;
  border-radius: 20px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  margin: 0 2px;
  
  /* Неоморфный стиль */
  background: ${props => props.selected 
    ? (props.type === 'income' ? 'linear-gradient(145deg, #e0f0ff, #d5e6f5)' : 'linear-gradient(145deg, #ffe0b3, #f5d6a9)')
    : 'linear-gradient(145deg, #ffffff, #f5f6fa)'};
  
  color: ${props => props.selected 
    ? (props.type === 'income' ? INCOME_TEXT : EXPENSE_TEXT)
    : '#555'};
  
  box-shadow: ${props => props.selected
    ? 'inset 2px 2px 5px rgba(0, 0, 0, 0.05), inset -2px -2px 5px rgba(255, 255, 255, 0.5)'
    : '2px 2px 5px rgba(0, 0, 0, 0.03), -2px -2px 5px rgba(255, 255, 255, 0.8)'};
  
  @media (max-width: 480px) {
    flex-shrink: 0;
    margin-right: 6px;
    
    /* Последний элемент имеет увеличенный отступ справа */
    &:last-child {
      margin-right: 24px;
    }
    
    /* Более явное выделение для выбранной категории */
    ${props => props.selected && `
      transform: scale(1.05);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    `}
  }
`;

// Визуальный индикатор состояния прокрутки в стиле пагинации
const ScrollPagination = styled.div`
  display: none;
  
  @media (max-width: 480px) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    pointer-events: none;
  }
`;

// Индикатор точки пагинации
const ScrollDot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${props => props.active 
    ? (props.type === 'income' ? INCOME_TEXT : EXPENSE_TEXT) 
    : 'rgba(0, 0, 0, 0.15)'};
  transition: all 0.2s;
  opacity: ${props => props.active ? 1 : 0.5};
`;



// Обновленный заголовок раздела с небольшим индикатором прокрутки
const CategorySectionTitle = styled(SectionTitle)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  
  @media (max-width: 480px) {
    &:after {
      content: "›";
      display: ${props => props.hasMoreItems ? 'block' : 'none'};
      margin-right: 4px;
      font-size: 18px;
      color: ${props => props.type === 'income' ? INCOME_TEXT : EXPENSE_TEXT};
      opacity: 0.7;
      transform: rotate(90deg);
    }
  }
`;



// Контейнер секции категорий
const CategorySection = styled(FormGroup)`
  position: relative;
  overflow: visible;
`;


// Улучшенная кнопка отправки формы в неоморфном стиле
const SubmitButton = styled.button`
  width: 100%;
  height: 52px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 10px;
  
  /* Градиентный фон для кнопок */
  background: ${props => props.type === 'income' 
    ? 'linear-gradient(145deg, #4189c6, #3571a7)'
    : 'linear-gradient(145deg, #d04a3f, #b43e35)'};
  
  color: white;
  box-shadow: 
    4px 4px 8px rgba(0, 0, 0, 0.1),
    -2px -2px 6px rgba(255, 255, 255, 0.05);
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 
      5px 5px 10px rgba(0, 0, 0, 0.12),
      -2px -2px 6px rgba(255, 255, 255, 0.05);
  }
  
  &:active {
    transform: translateY(0) scale(0.98);
    box-shadow: 
      2px 2px 4px rgba(0, 0, 0, 0.1),
      -1px -1px 4px rgba(255, 255, 255, 0.05);
  }
`;

// Обновленная иконка для календаря
const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" 
          stroke="#4a6fa1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M16 2V6" stroke="#4a6fa1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 2V6" stroke="#4a6fa1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 10H21" stroke="#4a6fa1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TransactionModal = ({ isOpen, onClose, type, onAddTransaction }) => {
  const { t } = useTranslation();
  const { telegramUserId } = useTelegram();
  const { userCurrency } = useCurrency();
    useEffect(() => {
    if (userCurrency) {
      console.log("Получена валюта пользователя из контекста:", userCurrency);
    } else {
      console.log("Валюта пользователя не задана в контексте");
    }
  }, [userCurrency]);
  
  // Корректное получение текущей даты с учетом локального часового пояса
  const getTodayDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Состояние формы
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [date, setDate] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currency, setCurrency] = useState('');
  const [categories, setCategories] = useState([]);
  const amountInputRef = useRef(null);
  const dateInputRef = useRef(null);
  
  // Валюты для выбора
const currencies = [
  { value: 'USD', symbol: '$' },
  { value: 'EUR', symbol: '€' },
  { value: 'UAH', symbol: '₴' },
  { value: 'RUB', symbol: '₽' },
  { value: 'GBP', symbol: '£' },
  { value: 'JPY', symbol: '¥' },
  { value: 'CNY', symbol: '¥' },
  { value: 'INR', symbol: '₹' },
  { value: 'CAD', symbol: 'C$' },
  { value: 'AUD', symbol: 'A$' },
  { value: 'BRL', symbol: 'R$' },
  { value: 'CHF', symbol: 'Fr' },
  { value: 'PLN', symbol: 'zł' },
  { value: 'TRY', symbol: '₺' }
];
// Исправленный компонент CategoriesSection с корректным расположением точек пагинации
const CategoriesSection = ({ categories, selectedCategory, setSelectedCategory, type, t }) => {
  const containerRef = useRef(null);
  const [hasMoreItems, setHasMoreItems] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [visibleSections, setVisibleSections] = useState(1);
  
  // Проверка наличия прокрутки
  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const hasOverflow = container.scrollWidth > container.clientWidth;
        setHasMoreItems(hasOverflow);
        
        // Расчет примерного количества секций
        if (hasOverflow) {
          const approxSections = Math.ceil(container.scrollWidth / container.clientWidth);
          setVisibleSections(Math.min(approxSections, 5));
        }
      }
    };
    
    // Задержка, чтобы DOM успел обновиться
    const timer = setTimeout(checkOverflow, 100);
    window.addEventListener('resize', checkOverflow);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [categories]);
  
  // Обработчик прокрутки для обновления точек пагинации

  
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);
  
  // Автоматическая прокрутка к выбранной категории
  const scrollToSelected = (target) => {
    if (containerRef.current && target) {
      const container = containerRef.current;
      container.scrollTo({
        left: target.offsetLeft - 10,
        behavior: 'smooth'
      });
    }
  };
  
  // Обработчик выбора категории
  const handleCategorySelect = (category, event) => {
    setSelectedCategory(category);
    if (event.currentTarget) {
      scrollToSelected(event.currentTarget);
    }
  };
  
// Улучшенный алгоритм расчёта активной точки с учётом различий устройств
const getActiveDot = () => {
  if (visibleSections <= 1) return 0;
  if (!containerRef.current) return 0;
  
  const container = containerRef.current;
  const maxScrollLeft = container.scrollWidth - container.clientWidth;
  
  // Если прокрутки нет или почти завершена
  if (maxScrollLeft <= 0) return 0;
  if (container.scrollLeft >= maxScrollLeft - 5) return visibleSections - 1;
  
  // Полностью нормализованное вычисление позиции
  const normalizedProgress = Math.min(container.scrollLeft / maxScrollLeft, 1);
  const dotIndex = Math.min(
    Math.floor(normalizedProgress * visibleSections),
    visibleSections - 1
  );
  
  // Если почти в конце, показываем последнюю точку
  if (normalizedProgress > 0.95) {
    return visibleSections - 1;
  }
  
  // Ограничиваем значение в случае переполнения
  return Math.min(dotIndex, visibleSections - 1);
};

// Добавим обработку предельных значений в функции handleScroll
const handleScroll = () => {
  if (containerRef.current) {
    const container = containerRef.current;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    
    if (maxScrollLeft > 0) {
      // Проверка для определения предельных положений
      if (container.scrollLeft >= maxScrollLeft - 5) {
        // Крайнее правое положение (с допуском в 5px)
        setScrollProgress(100);
      } else {
        // Нормальный случай
        const progress = (container.scrollLeft / maxScrollLeft) * 100;
        setScrollProgress(progress);
      }
    }
  }
};

  return (
    <CategorySection>
      <SectionTitle>
        {t('categories')}:
        
      </SectionTitle>
      
      {/* Обернем контейнер категорий в относительно позиционированный div */}
      <div style={{ position: 'relative', marginBottom: '16px' }}>
        <CategoryChips 
          ref={containerRef}
          hasMoreItems={hasMoreItems}
          type={type}
        >
          {categories.map((category) => (
            <CategoryChip
              key={category.name}
              selected={selectedCategory === category.name}
              onClick={(e) => handleCategorySelect(category.name, e)}
              type={type}
            >
              {category.name}
            </CategoryChip>
          ))}
        </CategoryChips>
        
        {/* Точки пагинации вынесены за пределы прокручиваемого контейнера */}
        {hasMoreItems && visibleSections > 1 && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            gap: '4px',
            height: '4px',
            pointerEvents: 'none'
          }}>
            {Array.from({ length: visibleSections }, (_, i) => (
              <div 
                key={i}
                style={{
                  width: '4px',
                  height: '4px',
                  borderRadius: '50%',
                  background: i === getActiveDot() 
                    ? (type === 'income' ? INCOME_TEXT : EXPENSE_TEXT) 
                    : 'rgba(0, 0, 0, 0.15)',
                  opacity: i === getActiveDot() ? 1 : 0.5,
                  transition: 'all 0.2s'
                }}
              />
            ))}
          </div>
        )}
      </div>
    </CategorySection>
  );
};
useEffect(() => {
  if (isOpen) {
    // Функция для предотвращения горизонтальной прокрутки
    const preventHorizontalScroll = (e) => {
      // Если есть горизонтальная прокрутка, блокируем ее
      if (e.target.scrollWidth > e.target.clientWidth) {
        e.preventDefault();
      }
    };
    
    // Находим все элементы с прокруткой внутри модального окна
    const modalElement = document.querySelector('.modal');
    if (modalElement) {
      modalElement.addEventListener('wheel', preventHorizontalScroll, { passive: false });
      
      // Очистка при закрытии
      return () => {
        modalElement.removeEventListener('wheel', preventHorizontalScroll);
      };
    }
  }
}, [isOpen]);
  // Инициализация состояний при открытии модального окна
  useEffect(() => {
    if (isOpen) {
      setAmount('');
      setComment('');
      
      // Получаем свежую текущую дату при каждом открытии
      const freshTodayDate = getTodayDate();
      setDate(freshTodayDate);
      
      console.log("Установлена сегодняшняя дата:", freshTodayDate);
      
      setSelectedCategory('');
      if (userCurrency) {
      console.log("Установлена валюта пользователя:", userCurrency);
      setCurrency(userCurrency);
    } else {
      // Используем USD как запасной вариант, если валюта пользователя недоступна
      console.log("Установлена запасная валюта: USD");
      setCurrency('USD');
    }
      
      // Установить фокус на поле ввода суммы
      setTimeout(() => {
        if (amountInputRef.current) {
          amountInputRef.current.focus();
        }
      }, 150);
      
      // Загрузить категории при открытии
      loadCategories();
    }
  }, [isOpen, userCurrency]);
  
  // Загрузка категорий с сервера - обновлена для использования userID из контекста
  const loadCategories = async () => {
    try {
      const userId = getUserId() || telegramUserId;
      
      const response = await fetch(
        `https://fftg.farodinprojects.org:3000/categories?user_id=${userId}&type=${type}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
        
        // Если есть категории, выбираем первую по умолчанию
        if (data.length > 0) {
          setSelectedCategory(data[0].name);
        }
      } else {
        console.error("Ошибка загрузки категорий.");
      }
    } catch (error) {
      console.error("Ошибка при загрузке категорий:", error);
    }
  };
  
  // Форматирование отображаемой даты
  const formatDisplayDate = (dateString) => {
    // Парсинг даты с учетом локальной временной зоны
    const parts = dateString.split('-');
    const selectedDateObj = new Date(
      parseInt(parts[0], 10),
      parseInt(parts[1], 10) - 1, // месяцы начинаются с 0
      parseInt(parts[2], 10)
    );
    selectedDateObj.setHours(0, 0, 0, 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Сравниваем только дату, игнорируя время
    const isSameDay = (date1, date2) => {
      return date1.getFullYear() === date2.getFullYear() &&
             date1.getMonth() === date2.getMonth() &&
             date1.getDate() === date2.getDate();
    };
    
    if (isSameDay(selectedDateObj, today)) {
      return t('today');
    } else if (isSameDay(selectedDateObj, yesterday)) {
      return t('yesterday');
    } else {
      const options = { day: 'numeric', month: 'short' };
      return selectedDateObj.toLocaleDateString(undefined, options);
    }
  };
  
  // Обработчик изменения даты
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    console.log("Выбрана дата:", newDate);
    
    // Используем метод сравнения дат, не зависящий от часового пояса
    const parts = newDate.split('-');
    const selectedDateObj = new Date(
      parseInt(parts[0], 10),
      parseInt(parts[1], 10) - 1,
      parseInt(parts[2], 10)
    );
    selectedDateObj.setHours(0, 0, 0, 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDateObj > today) {
      alert(t('future_date_error'));
      // Используем свежую сегодняшнюю дату
      setDate(getTodayDate());
    } else {
      setDate(newDate);
    }
  };
  
  // Открытие нативного селектора даты
  const openDatePicker = () => {
    if (dateInputRef.current) {
      try {
        if (typeof dateInputRef.current.showPicker === 'function') {
          dateInputRef.current.showPicker();
        } else {
          dateInputRef.current.click();
        }
      } catch (error) {
        dateInputRef.current.click();
      }
    }
  };
  
  // Обработчик добавления транзакции - обновленный для использования ID из контекста
  const handleAddTransaction = async () => {
    // Валидация
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      alert(t('enter_valid_amount'));
      return;
    }
    
    if (!selectedCategory) {
      alert(t('select_category'));
      return;
    }
    
    // Формирование даты транзакции
    let transactionDate = date;
    // Получаем актуальную текущую дату
    const freshTodayDate = getTodayDate();
    
    // Если это сегодняшняя дата, добавляем текущее время
    if (transactionDate === freshTodayDate) {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      transactionDate = `${transactionDate} ${hours}:${minutes}:${seconds}`;
    } else {
      // Для прошлых дат используем полдень
      transactionDate = `${transactionDate} 12:00:00`;
    }
    
    // Передаем данные в родительский компонент
    onAddTransaction({
      amount: parseFloat(amount),
      category: selectedCategory,
      comment: comment.trim(),
      date: transactionDate,
      currency: currency
    });
    
    // Закрываем модальное окно
    onClose();
  };
  
  // Определение заголовка модального окна в зависимости от типа
  const modalTitle = type === 'income' ? t('add_income') : t('add_expense');
  
  if (!isOpen) return null;
  
  return (
    <div className={styles.modal}>
<ModalContainer>
    <TelegramModal>
      <ModalHeader type={type}>
        <ModalTitle>{modalTitle}</ModalTitle>
        <CloseIcon type={type} onClick={onClose}>&times;</CloseIcon>
      </ModalHeader>
      
      <ModalBody>
          {/* Поле ввода суммы с идеально интегрированным селектором валюты справа */}
          <AmountInputContainer>
            <AmountInputWrapper type={type}>
              <AmountInput
                ref={amountInputRef}
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={t('enterAmountPlaceholder')}
                min="0.01"
                step="0.01"
                inputMode="decimal"
              />
              <CurrencySelector
                value={currency || userCurrency || 'USD'} // Гарантируем, что значение всегда установлено
                onChange={(e) => {
                  console.log("Выбрана валюта:", e.target.value);
                  setCurrency(e.target.value);
                }}
                type={type}
              >
                {currencies.map(curr => (
                  <option key={curr.value} value={curr.value}>
                    {curr.symbol}
                  </option>
                ))}
              </CurrencySelector>
            </AmountInputWrapper>
          </AmountInputContainer>
          
          {/* Кнопка выбора даты - Используем актуальное значение max */}
          <DateButton onClick={openDatePicker}>
            <CalendarIcon />
            {formatDisplayDate(date)}
            <DateInput
              ref={dateInputRef}
              type="date"
              value={date}
              onChange={handleDateChange}
              max={getTodayDate()} // Всегда получаем свежую дату
            />
          </DateButton>
          
          {/* Категории */}
          <CategoriesSection 
            categories={categories}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            type={type}
            t={t}
          />
          
          {/* Поле для комментария */}
          <FormGroup>
            <CommentTextarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('commentPlaceholder')}
              type={type}
            />
          </FormGroup>
          
          {/* Кнопка добавления */}
          <SubmitButton
            type={type}
            onClick={handleAddTransaction}
          >
            {t('addButton')}
          </SubmitButton>
     </ModalBody>
    </TelegramModal>
  </ModalContainer>
    </div>
  );
};

export default TransactionModal;